import { useQuery } from '@tanstack/react-query';
import {
  getAllExchangeRates,
  type ExchangeRate,
} from '@/services/ant-design-pro/exchangeRates';

/**
 * Exchange rate fallback defaults
 */
const FALLBACK_RATES = {
  CNY: 210.78,
  USD: 1300.25,
  JPY: 8.75,
  EUR: 1420.50,
};

const EXCHANGE_RATES_STORAGE_KEY = 'exchangeRates';

/** 위안화 환율 갈래: 해외직구(CNY) · 라이브(CNY_LIVE) · 자사몰(CNY_OWNMALL). 라이브·자사몰 값이 없으면 CNY. */
export type CnyRateSegment = 'overseas' | 'live' | 'ownmall';
const CNY_SEGMENT_CURRENCY: Record<CnyRateSegment, string> = {
  overseas: 'CNY',
  live: 'CNY_LIVE',
  ownmall: 'CNY_OWNMALL',
};

/** 상품 productType('live'|'own') → 환율 갈래 */
export const cnySegmentForProductType = (productType?: string | null): CnyRateSegment =>
  productType === 'live' ? 'live' : 'ownmall';

type StoredExchangeRates = typeof FALLBACK_RATES & { CNY_LIVE?: number; CNY_OWNMALL?: number };

export const EXCHANGE_RATES_QUERY_KEY = ['exchangeRates'] as const;

/**
 * Get exchange rates from localStorage
 */
export const getExchangeRatesFromStorage = (): StoredExchangeRates => {
  try {
    const stored = localStorage.getItem(EXCHANGE_RATES_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        CNY: parsed.CNY ?? FALLBACK_RATES.CNY,
        USD: parsed.USD ?? FALLBACK_RATES.USD,
        JPY: parsed.JPY ?? FALLBACK_RATES.JPY,
        EUR: parsed.EUR ?? FALLBACK_RATES.EUR,
        CNY_LIVE: parsed.CNY_LIVE > 0 ? parsed.CNY_LIVE : undefined,
        CNY_OWNMALL: parsed.CNY_OWNMALL > 0 ? parsed.CNY_OWNMALL : undefined,
      };
    }
  } catch (error) {
    // console.warn('Failed to parse exchange rates from localStorage:', error);
  }
  return FALLBACK_RATES;
};

/**
 * Save exchange rates to localStorage
 */
export const saveExchangeRatesToStorage = (rates: StoredExchangeRates): void => {
  try {
    localStorage.setItem(EXCHANGE_RATES_STORAGE_KEY, JSON.stringify(rates));
  } catch (error) {
    // console.error('Failed to save exchange rates to localStorage:', error);
  }
};

/** Parse admin GET /admin/exchange-rates response (umi may wrap shapes differently). */
export function parseExchangeRatesFromApiResponse(response: unknown): ExchangeRate[] {
  if (!response || typeof response !== 'object') return [];

  const res = response as Record<string, unknown>;
  if (res.success === false) return [];

  const data = res.data;
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const nested = (data as Record<string, unknown>).exchangeRates;
    if (Array.isArray(nested)) return nested as ExchangeRate[];
  }
  if (Array.isArray(data)) return data as ExchangeRate[];
  if (Array.isArray(res.exchangeRates)) return res.exchangeRates as ExchangeRate[];
  return [];
}

function exchangeRatesToStorageMap(rates: ExchangeRate[]): StoredExchangeRates {
  const map: Record<string, number> = {};
  rates.forEach((rate) => {
    if (rate.currency && rate.rate > 0) map[rate.currency] = rate.rate;
  });
  return {
    CNY: map.CNY ?? FALLBACK_RATES.CNY,
    USD: map.USD ?? FALLBACK_RATES.USD,
    JPY: map.JPY ?? FALLBACK_RATES.JPY,
    EUR: map.EUR ?? FALLBACK_RATES.EUR,
    CNY_LIVE: map.CNY_LIVE,
    CNY_OWNMALL: map.CNY_OWNMALL,
  };
}

/**
 * Fetch exchange rates from API and persist to localStorage.
 */
export async function fetchExchangeRatesAndCache(): Promise<ExchangeRate[]> {
  const response = await getAllExchangeRates();
  const rates = parseExchangeRatesFromApiResponse(response);
  if (rates.length > 0) {
    saveExchangeRatesToStorage(exchangeRatesToStorageMap(rates));
  }
  return rates;
}

/**
 * Initialize exchange rates - fetch from backend and save to localStorage
 * Call this on app startup
 */
export const initializeExchangeRates = async (): Promise<void> => {
  try {
    await fetchExchangeRatesAndCache();
  } catch (error) {
    // console.warn('Failed to initialize exchange rates, using localStorage or fallback:', error);
  }
};

/**
 * Custom hook to use exchange rates from localStorage only
 * Does NOT fetch from API - rates should be updated via Rates page
 * Returns rates for all currencies (CNY, USD, JPY, EUR) to KRW
 */
export const useExchangeRates = () => {
  // Only read from localStorage, no API calls
  const rates = getExchangeRatesFromStorage();

  return {
    CNY: rates.CNY ?? FALLBACK_RATES.CNY,
    USD: rates.USD ?? FALLBACK_RATES.USD,
    JPY: rates.JPY ?? FALLBACK_RATES.JPY,
    EUR: rates.EUR ?? FALLBACK_RATES.EUR,
    isLoading: false, // No loading since we're reading from localStorage
  };
};

/** API 환율 목록에서 갈래별 위안화 환율(라이브·자사몰 값이 없으면 CNY). 못 찾으면 undefined. */
export function pickCnyRateForSegment(
  rates: Array<{ currency: string; rate: number }> | null | undefined,
  segment: CnyRateSegment = 'overseas',
): number | undefined {
  const find = (currency: string) => rates?.find((r) => r.currency === currency && r.rate > 0)?.rate;
  return find(CNY_SEGMENT_CURRENCY[segment]) ?? find('CNY');
}

/**
 * CNY → KRW rate for admin UI (e.g. live product cost conversion).
 * Always refetches from DB on mount; localStorage is display-only until fetch completes.
 * segment: 라이브 상품은 'live', 자사몰 상품은 'ownmall' (값이 없으면 해외직구 CNY).
 */
export const useCNYExchangeRate = (
  fallbackRate: number = 210.78,
  segment: CnyRateSegment = 'overseas',
) => {
  const cached = getExchangeRatesFromStorage();
  const cachedRate =
    (segment === 'live' ? cached.CNY_LIVE : segment === 'ownmall' ? cached.CNY_OWNMALL : undefined) ??
    cached.CNY;

  const { data: rates } = useQuery({
    queryKey: EXCHANGE_RATES_QUERY_KEY,
    queryFn: fetchExchangeRatesAndCache,
    refetchOnMount: 'always',
    staleTime: 0,
    retry: 1,
  });

  const cnyFromApi = pickCnyRateForSegment(rates, segment);

  return {
    rate: cnyFromApi ?? cachedRate ?? fallbackRate,
    isLoading: rates == null && cnyFromApi == null,
  };
};

/**
 * Convert CNY amount to target currency
 */
export const convertCNYToCurrency = (
  cnyAmount: number,
  targetCurrency: 'KRW' | 'USD' | 'JPY' | 'EUR',
  rates: { CNY: number; USD: number; JPY: number; EUR: number }
): number => {
  if (targetCurrency === 'KRW') {
    return cnyAmount * rates.CNY;
  } else if (targetCurrency === 'USD') {
    // Convert CNY -> KRW -> USD
    const krwAmount = cnyAmount * rates.CNY;
    return krwAmount / rates.USD;
  } else if (targetCurrency === 'JPY') {
    // Convert CNY -> KRW -> JPY
    const krwAmount = cnyAmount * rates.CNY;
    return krwAmount / rates.JPY;
  } else if (targetCurrency === 'EUR') {
    // Convert CNY -> KRW -> EUR
    const krwAmount = cnyAmount * rates.CNY;
    return krwAmount / rates.EUR;
  }
  return cnyAmount;
};
